'use client';

import React from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import BigNumber from 'bignumber.js';
import { FiCopy, FiCheck, FiInbox } from 'react-icons/fi';
import styles from './AddressDetailPage.module.css';
import useApiQuery from 'lib/api/useApiQuery';
import useQueryWithPages from 'src/hooks/useQueryWithPages';
import { formatTimestamp } from '../../utils/formatTimestamp';

const AddressDetailPage: React.FC = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const hash = params?.hash as string;
  const tab = searchParams?.get('tab') || 'txs';
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const addressQuery = useApiQuery('address', {
    pathParams: { hash },
    queryOptions: { enabled: Boolean(hash) },
  });

  const countersQuery = useApiQuery('address_counters', {
    pathParams: { hash },
    queryOptions: { enabled: Boolean(hash) },
  });

  const txsQuery = useQueryWithPages({
    resourceName: 'address_txs',
    pathParams: { hash },
    filters: { filter: 'to' },
    options: { enabled: Boolean(hash) && tab === 'txs' },
  });

  const tokenTransfersQuery = useQueryWithPages({
    resourceName: 'address_token_transfers',
    pathParams: { hash },
    options: { enabled: Boolean(hash) && tab === 'token_transfers' },
  });

  const internalTxsQuery = useQueryWithPages({
    resourceName: 'address_internal_txs',
    pathParams: { hash },
    options: { enabled: Boolean(hash) && tab === 'internal_txs' },
  });

  const address = addressQuery.data as any;
  const counters = countersQuery.data as any;

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatWei = (value: string | null | undefined) => {
    if (!value || value === '0') return '0 OGPU';
    const bn = BigNumber(value);
    const eth = bn.dividedBy(BigNumber(10).pow(18));
    return `${eth.toFixed(6)} OGPU`;
  };

  const isAddressLoading = addressQuery.isLoading;
  const isCountersLoading = countersQuery.isLoading;

  if (!isAddressLoading && !address) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Address not found</div>
      </div>
    );
  }

  const currentQuery = tab === 'token_transfers' ? tokenTransfersQuery : tab === 'internal_txs' ? internalTxsQuery : txsQuery;
  const items = currentQuery.data?.items || [];

  const skeleton = (width = '120px') => (
    <span className={styles.skeleton} style={{ width }} />
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {isAddressLoading ? 'Address' : address.is_contract ? 'Contract' : 'Address'}
        </h1>
        {!isAddressLoading && address.is_contract ? (
          <span className={`${styles.typeBadge} ${styles.contractBadge}`}>Contract</span>
        ) : !isAddressLoading ? (
          <span className={styles.typeBadge}>EOA</span>
        ) : null}
        {!isAddressLoading && address.is_verified && (
          <span className={styles.typeBadge} style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>Verified</span>
        )}
      </div>

      <div className={styles.detailCard}>
        <div className={styles.row}>
          <div className={styles.label}>Address</div>
          <div className={styles.value}>
            <span style={{ fontFamily: 'monospace' }}>{hash}</span>
            <button
              className={styles.copyButton}
              onClick={() => copyToClipboard(hash, 'address')}
            >
              {copiedField === 'address' ? <FiCheck size={14} /> : <FiCopy size={14} />}
            </button>
          </div>
        </div>

        {address?.name && (
          <div className={styles.row}>
            <div className={styles.label}>Name</div>
            <div className={styles.value}>{address.name}</div>
          </div>
        )}

        {address?.ens_domain_name && (
          <div className={styles.row}>
            <div className={styles.label}>ENS</div>
            <div className={styles.value}>{address.ens_domain_name}</div>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.label}>Balance</div>
          <div className={styles.value}>
            {isAddressLoading ? skeleton('180px') : formatWei(address.coin_balance)}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Transactions</div>
          <div className={styles.value}>
            {isCountersLoading ? skeleton('80px') : Number(counters?.transactions_count || 0).toLocaleString()}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Token Transfers</div>
          <div className={styles.value}>
            {isCountersLoading ? skeleton('80px') : Number(counters?.token_transfers_count || 0).toLocaleString()}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Gas Used</div>
          <div className={styles.value}>
            {isCountersLoading ? skeleton('100px') : Number(counters?.gas_usage_count || 0).toLocaleString()}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Last Balance Update</div>
          <div className={styles.value}>
            {isAddressLoading ? skeleton('100px') : address.block_number_balance_updated_at ? (
              <>
                Block{' '}
                <a href={`/block/${address.block_number_balance_updated_at}`} className={styles.link}>
                  {address.block_number_balance_updated_at}
                </a>
              </>
            ) : '—'}
          </div>
        </div>

        {(isAddressLoading || address?.creator_address_hash) && (
          <div className={styles.row}>
            <div className={styles.label}>Creator</div>
            <div className={styles.value}>
              {isAddressLoading ? skeleton('200px') : (
                <>
                  <a href={`/address/${address.creator_address_hash}`} className={styles.link}>
                    {address.creator_address_hash}
                  </a>
                  {address.creation_transaction_hash && (
                    <span style={{ marginLeft: 8, fontSize: '0.8125rem', color: 'rgba(255,255,255,0.4)' }}>
                      at txn{' '}
                      <a href={`/tx/${address.creation_transaction_hash}`} className={styles.link}>
                        {address.creation_transaction_hash.slice(0, 10)}...
                      </a>
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <a
          href={`/address/${hash}?tab=txs`}
          className={`${styles.tab} ${tab === 'txs' ? styles.tabActive : ''}`}
        >
          Transactions
        </a>
        <a
          href={`/address/${hash}?tab=token_transfers`}
          className={`${styles.tab} ${tab === 'token_transfers' ? styles.tabActive : ''}`}
        >
          Token Transfers
        </a>
        <a
          href={`/address/${hash}?tab=internal_txs`}
          className={`${styles.tab} ${tab === 'internal_txs' ? styles.tabActive : ''}`}
        >
          Internal Txns
        </a>
      </div>

      {/* Transactions Table */}
      {tab === 'txs' && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Txn Hash</th>
                <th>Method</th>
                <th>Block</th>
                <th>Age</th>
                <th>Direction</th>
                <th>From/To</th>
                <th>Value OGPU</th>
                <th>Fee OGPU</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !currentQuery.isLoading && (
                <tr>
                  <td colSpan={8}>
                    <div className={styles.emptyState}>
                      <FiInbox className={styles.emptyIcon} size={48} />
                      <p>No transactions found</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map((tx: any, i: number) => {
                const isIn = tx.to?.hash?.toLowerCase() === hash?.toLowerCase();
                return (
                  <tr key={tx.hash || i}>
                    <td>
                      <a href={`/tx/${tx.hash}`} className={styles.link}>
                        {tx.hash?.slice(0, 10)}...{tx.hash?.slice(-4)}
                      </a>
                    </td>
                    <td>
                      <span className={styles.method}>{tx.method || 'Transfer'}</span>
                    </td>
                    <td>
                      <a href={`/block/${tx.block_number}`} className={styles.link}>
                        {tx.block_number}
                      </a>
                    </td>
                    <td>
                      <span className={styles.age}>{formatTimestamp(tx.timestamp)}</span>
                    </td>
                    <td>
                      <span className={`${styles.inOut} ${isIn ? styles.inBadge : styles.outBadge}`}>
                        {isIn ? 'IN' : 'OUT'}
                      </span>
                    </td>
                    <td>
                      {isIn ? (
                        <a href={`/address/${tx.from?.hash}`} className={styles.link}>
                          {tx.from?.hash?.slice(0, 8)}...{tx.from?.hash?.slice(-4)}
                        </a>
                      ) : (
                        <a href={`/address/${tx.to?.hash}`} className={styles.link}>
                          {tx.to?.hash?.slice(0, 8)}...{tx.to?.hash?.slice(-4)}
                        </a>
                      )}
                    </td>
                    <td>{formatWeiShort(tx.value)}</td>
                    <td style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.5)' }}>
                      {formatWeiShort(tx.fee?.value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Token Transfers Table */}
      {tab === 'token_transfers' && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Txn Hash</th>
                <th>Block</th>
                <th>Age</th>
                <th>From</th>
                <th>To</th>
                <th>Amount</th>
                <th>Token</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !currentQuery.isLoading && (
                <tr>
                  <td colSpan={7}>
                    <div className={styles.emptyState}>
                      <FiInbox className={styles.emptyIcon} size={48} />
                      <p>No token transfers found</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map((t: any, i: number) => (
                <tr key={`${t.transaction_hash}-${i}`}>
                  <td>
                    <a href={`/tx/${t.transaction_hash}`} className={styles.link}>
                      {t.transaction_hash?.slice(0, 10)}...{t.transaction_hash?.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <a href={`/block/${t.block_number}`} className={styles.link}>
                      {t.block_number}
                    </a>
                  </td>
                  <td className={styles.age}>{formatTimestamp(t.timestamp)}</td>
                  <td>
                    <a href={`/address/${t.from?.hash}`} className={styles.link}>
                      {t.from?.hash?.slice(0, 8)}...{t.from?.hash?.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <a href={`/address/${t.to?.hash}`} className={styles.link}>
                      {t.to?.hash?.slice(0, 8)}...{t.to?.hash?.slice(-4)}
                    </a>
                  </td>
                  <td>{formatTokenAmount(t.total?.value, t.total?.decimals)}</td>
                  <td>
                    <a href={`/token/${t.token?.address}`} className={styles.link}>
                      {t.token?.symbol || t.token?.name || 'Token'}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Internal Transactions Table */}
      {tab === 'internal_txs' && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Parent Txn Hash</th>
                <th>Type</th>
                <th>Block</th>
                <th>From</th>
                <th>To</th>
                <th>Value OGPU</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !currentQuery.isLoading && (
                <tr>
                  <td colSpan={6}>
                    <div className={styles.emptyState}>
                      <FiInbox className={styles.emptyIcon} size={48} />
                      <p>No internal transactions found</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map((itx: any, i: number) => (
                <tr key={`${itx.transaction_hash}-${i}`}>
                  <td>
                    <a href={`/tx/${itx.transaction_hash}`} className={styles.link}>
                      {itx.transaction_hash?.slice(0, 10)}...{itx.transaction_hash?.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <span className={styles.badge}>{itx.type || 'call'}</span>
                  </td>
                  <td>
                    <a href={`/block/${itx.block_number}`} className={styles.link}>
                      {itx.block_number}
                    </a>
                  </td>
                  <td>
                    <a href={`/address/${itx.from?.hash}`} className={styles.link}>
                      {itx.from?.hash?.slice(0, 8)}...{itx.from?.hash?.slice(-4)}
                    </a>
                  </td>
                  <td>
                    <a href={`/address/${itx.to?.hash}`} className={styles.link}>
                      {itx.to?.hash?.slice(0, 8)}...{itx.to?.hash?.slice(-4)}
                    </a>
                  </td>
                  <td>{formatWeiShort(itx.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {currentQuery.isLoading && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={`skel-${i}`}>
                  {[...Array(tab === 'txs' ? 8 : tab === 'token_transfers' ? 7 : 6)].map((_, j) => (
                    <td key={j}><span className={styles.skeleton} style={{ width: j === 0 ? '140px' : '80px' }} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!currentQuery.isLoading && items.length > 0 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            disabled={!currentQuery.pagination.canGoBackwards}
            onClick={currentQuery.pagination.onPrevPageClick}
          >
            &lt;
          </button>
          <button className={`${styles.pageButton} ${styles.pageButtonActive}`}>
            {currentQuery.pagination.page}
          </button>
          <button
            className={styles.pageButton}
            disabled={!currentQuery.pagination.hasNextPage}
            onClick={currentQuery.pagination.onNextPageClick}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
};

function formatWeiShort(value: string | null | undefined) {
  if (!value || value === '0') return '0';
  const bn = BigNumber(value);
  const eth = bn.dividedBy(BigNumber(10).pow(18));
  if (eth.isGreaterThanOrEqualTo(1000000)) return `${eth.dividedBy(1000000).toFixed(2)}M`;
  if (eth.isGreaterThanOrEqualTo(1000)) return `${eth.dividedBy(1000).toFixed(2)}K`;
  if (eth.isLessThan(0.000001) && !eth.isZero()) return eth.toExponential(2);
  return eth.toFixed(6);
}

function formatTokenAmount(value: string | null | undefined, decimals: string | null | undefined) {
  if (!value || value === '0') return '0';
  const dec = decimals ? Number(decimals) : 18;
  const bn = BigNumber(value);
  const amount = bn.dividedBy(BigNumber(10).pow(dec));
  if (amount.isGreaterThanOrEqualTo(1000000)) return `${amount.dividedBy(1000000).toFixed(2)}M`;
  if (amount.isGreaterThanOrEqualTo(1000)) return `${amount.dividedBy(1000).toFixed(2)}K`;
  return amount.toFixed(4);
}

export default AddressDetailPage;
